/**
 * WordPress dependencies
 */
import { Button, Icon } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useEntityRecords } from '@wordpress/core-data';
import { decodeEntities } from '@wordpress/html-entities';
import { useState, useMemo, Fragment, RawHTML } from '@wordpress/element';
import { dateI18n, getDate, getSettings } from '@wordpress/date';
import { DataViews } from '@wordpress/dataviews';
import { parse } from '@wordpress/block-serialization-default-parser';
import { mobile, tablet, desktop } from '@wordpress/icons';

import './style.scss';
import {
	trashPostAction,
	usePermanentlyDeletePostAction,
	useRestorePostAction,
	postRevisionsAction,
	editPostAction,
	editSettingsAction,
} from './actions';

/**
 * Internal dependencies
 */
import { Header } from '../components/header';
const EMPTY_ARRAY = [];
const STATUSES = [
	{ value: 'draft', label: __( 'Draft' ) },
	{ value: 'future', label: __( 'Scheduled' ) },
	{ value: 'pending', label: __( 'Pending Review' ) },
	{ value: 'private', label: __( 'Private' ) },
	{ value: 'publish', label: __( 'Published' ) },
	{ value: 'trash', label: __( 'Trash' ) },
];
const DEFAULT_STATUSES = 'draft,future,pending,private,publish'; // All but 'trash'.

export const Dashboard = () => {
	const [ view, setView ] = useState( {
		type: 'table',
		perPage: 10,
		page: 1,
		sort: {
			field: 'date',
			direction: 'desc',
		},
		search: '',
		filters: [],
		hiddenFields: [ 'date', 'featured-image' ],
		layout: {},
	} );

	const queryArgs = useMemo( () => {
		const filters = {};
		view.filters.forEach( ( filter ) => {
			if ( filter.field === 'status' && filter.operator === 'in' ) {
				filters.status = filter.value;
			}
			if ( filter.field === 'author' && filter.operator === 'in' ) {
				filters.author = filter.value;
			}
		} );
		// We want to provide a different default item for the status filter
		// than the REST API provides.
		if ( ! filters.status || filters.status === '' ) {
			filters.status = DEFAULT_STATUSES;
		}
		return {
			per_page: view.perPage,
			page: view.page,
			_embed: 'author',
			order: view.sort?.direction,
			orderby: view.sort?.field,
			search: view.search,
			...filters,
		};
	}, [ view ] );

	const {
		records: popups,
		isResolving: isLoadingPopups,
		totalItems,
		totalPages,
	} = useEntityRecords( 'postType', 'popper', queryArgs );

	const { records: authors } = useEntityRecords( 'root', 'user', {
		who: 'authors',
	} );

	const paginationInfo = useMemo(
		() => ( {
			totalItems,
			totalPages,
		} ),
		[ totalItems, totalPages ]
	);

	const fields = useMemo(
		() => [
			{
				header: __( 'Title' ),
				id: 'title',
				getValue: ( item ) => item.title?.rendered || item.slug,
				render: ( { item } ) => {
					return (
						<div>
							<a
								href={ `post.php?post=${ item.id }&action=edit` }
							>
								{ decodeEntities( item.title?.rendered ) ||
									__( '(no title)', 'popper' ) }
							</a>
							{ item.excerpt.rendered && (
								<p>{ item.excerpt.rendered }</p>
							) }
						</div>
					);
				},
				filters: [ { id: 'search', type: 'search' } ],
				maxWidth: 400,
				sortingFn: 'alphanumeric',
				enableHiding: false,
			},
			{
				header: __( 'Triggers' ),
				id: 'triggers',
				getValue: ( item ) => item.content.raw,
				render: ( { item } ) => {
					const block = parse( item.content.raw );
					if ( ! block ) {
						return <span></span>;
					}
					const triggers = block[ 0 ].attrs.triggers?.map( ( o ) => {
						const result = o.replace( /([A-Z])/g, ' $1' );
						const finalResult =
							result.charAt( 0 ).toUpperCase() +
							result.slice( 1 );

						return finalResult;
					} );
					return (
						<RawHTML>
							{ triggers?.map( ( o ) => {
								return `${ o }` + '<br />';
							} ) }
						</RawHTML>
					);
				},
				filters: [ { id: 'search', type: 'search' } ],
				maxWidth: 400,
				sortingFn: 'alphanumeric',
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Locations' ),
				id: 'locations',
				getValue: ( item ) => item.meta.popper_rules.locations,
				render: ( { item } ) => {
					return (
						<RawHTML>
							{ item.popper_locations.include.join( '<br /> ' ) }
						</RawHTML>
					);
				},
				filters: [ { id: 'search', type: 'search' } ],
				maxWidth: 400,
				sortingFn: 'alphanumeric',
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Exclude' ),
				id: 'esclude',
				getValue: ( item ) => item.meta.popper_rules.esclude,
				render: ( { item } ) => {
					return (
						<RawHTML>
							{ item.popper_locations.exclude.join( '<br /> ' ) }
						</RawHTML>
					);
				},
				filters: [ { id: 'search', type: 'search' } ],
				maxWidth: 400,
				sortingFn: 'alphanumeric',
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Devices' ),
				id: 'devices',
				getValue: ( item ) => item.meta.popper_rules.device,
				render: ( { item } ) => {
					return (
						<span>
							{ item.meta.popper_rules.device.includes(
								'mobile'
							) ? (
								<Icon icon={ mobile } />
							) : null }
							{ item.meta.popper_rules.device.includes(
								'tablet'
							) ? (
								<Icon icon={ tablet } />
							) : null }
							{ item.meta.popper_rules.device.includes(
								'desktop'
							) ? (
								<Icon icon={ desktop } />
							) : null }
						</span>
					);
				},
				filters: [ { id: 'search', type: 'search' } ],
				maxWidth: 400,
				sortingFn: 'alphanumeric',
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Users' ),
				id: 'users',
				getValue: ( item ) => item.meta.popper_rules.user,
				render: ( { item } ) => {
					return <RawHTML>{ item.popper_locations.user }</RawHTML>;
				},
				maxWidth: 400,
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Author' ),
				id: 'author',
				getValue: ( { item } ) => item._embedded?.author[ 0 ]?.name,
				type: 'enumeration',
				elements:
					authors?.map( ( { id, name } ) => ( {
						value: id,
						label: name,
					} ) ) || [],
			},
			{
				header: __( 'Status' ),
				id: 'status',
				getValue: ( { item } ) =>
					STATUSES.find( ( { value } ) => value === item.status )
						?.label ?? item.status,
				type: 'enumeration',
				elements: STATUSES,
				enableSorting: false,
				filterBy: {
					operators: [ 'in' ],
				},
			},
			{
				id: 'date',
				header: __( 'Date' ),
				getValue: ( { item } ) => item.date,
				render: ( { item } ) => {
					const formattedDate = dateI18n(
						getSettings().formats.datetimeAbbreviated,
						getDate( item.date )
					);
					return <time>{ formattedDate }</time>;
				},
				enableHiding: false,
			},
		],
		[ authors ]
	);

	const permanentlyDeletePostAction = usePermanentlyDeletePostAction();
	const restorePostAction = useRestorePostAction();
	const actions = useMemo(
		() => [
			trashPostAction,
			restorePostAction,
			permanentlyDeletePostAction,
			editPostAction,
			postRevisionsAction,
			editSettingsAction,
		],
		[ permanentlyDeletePostAction, restorePostAction ]
	);

	return (
		<Fragment>
			<Header title={ __( 'Popups', 'popper' ) }>
				<Button
					text={ __( 'Add new' ) }
					variant="primary"
					size="small"
					href={ 'post-new.php?post_type=popper' }
				/>
			</Header>
			<div className="popper-content">
				<DataViews
					data={ popups || EMPTY_ARRAY }
					paginationInfo={ paginationInfo }
					view={ view }
					fields={ fields }
					actions={ actions }
					isLoading={ isLoadingPopups }
					onChangeView={ setView }
					supportedLayouts={ [ 'table' ] }
				/>
			</div>
		</Fragment>
	);
};
